// Catalog.ID Client — Voucher API
// ============================================================================
// Registration vouchers (registration-restructure §7). Payment always mints
// voucher rows and registration always consumes one, which is what makes
// "buy one for someone else" and operator-minted (Stripe-less) codes possible.
//
// Note the split: `purchaseVouchers` is ANONYMOUS — a buyer needs no account,
// and the Worker-signed payment proof is the authorization — while the mint /
// list / revoke calls are the operator backoffice and ride the session token.

import { api } from './client';
import type { RegistrationType } from './payment';

export type { RegistrationType };

export interface VoucherPurchaseRequest {
    paymentProofToken: string;
    nonce: string;
    registrationType: RegistrationType;
}

export interface VoucherBatchResponse {
    registrationType: RegistrationType;
    quantity: number;
    expiresAt: string;
    /**
     * The organization this batch was recorded against, or null
     * (registration-restructure §13.4). Set when an owner/admin delegate buys
     * `delegate` vouchers under their session — the server reports what the ROWS
     * say, so an idempotent replay names the FIRST buyer's organization, not the
     * replaying caller's. A null here after an org purchase means the session did
     * not qualify and the codes are unbound stock.
     */
    organizationCid: string | null;
}

export interface VoucherMintResponse extends VoucherBatchResponse {
    /** Plaintext codes — the OPERATOR MINT only (registration-wallets §4.7).
     *  That path is the one place a server still produces codes, and they exist
     *  only for the length of this response. A PURCHASE returns none: the buyer's
     *  own client minted them and the node has never seen one. */
    codes: string[];
    vouchers: { voucherId: number; expiresAt: string }[];
    /** How many codes were asked for: a gift refused part-way returns fewer (apps/bitcash/docs/plans/bitcash-v2.md M12). */
    requested?: number;
    /** The gift each code carries, when the mint asked for one. */
    gift?: { reference: string; bit: number; founderBit: number | null } | null;
    /** The ledger's refusal that stopped a gift-funded mint, with the sentence to show. */
    refused?: { code: string; error: string } | null;
}

/** The literal row status, as the node stores it. The client folds expiry into
 *  it — expiry is a date on the row, not a status the server writes. */
export type VoucherInspectStatus = VoucherStatus;

/**
 * What one voucher lookup id names (registration-wallets §5) — and nothing
 * else. No bits, no lock key, no wallet: the node is blind to all three.
 */
export interface VoucherInspectResponse {
    registrationType: RegistrationType;
    status: VoucherInspectStatus;
    expiresAt: string;
}

export type VoucherStatus = 'unredeemed' | 'reserved' | 'consumed' | 'revoked' | 'refunded';

export interface VoucherSummary {
    voucherId: number;
    registrationType: RegistrationType;
    status: VoucherStatus;
    provenance: 'payment' | 'operator';
    /**
     * Opaque batch reference (registration-restructure §14.4, which reverses G4):
     * the payment Worker's HMAC of the checkout session under a secret the node
     * never holds. It identifies the batch a code came from — for support, for
     * spotting a refund — and cannot be turned back into a payment-provider id.
     * Null on operator mints, which have no payment behind them.
     */
    proofId: string | null;
    mintedByCid: string | null;
    /** Set only on rows an organization bought for its own invitations (§13.4). */
    organizationAccountId: number | null;
    note: string | null;
    amount: number;
    currency: string;
    redeemedByCid: string | null;
    createdAt: string;
    expiresAt: string;
    reservedAt: string | null;
    consumedAt: string | null;
    revokedAt: string | null;
}

export interface VoucherListResponse {
    total: number;
    limit: number;
    offset: number;
    vouchers: VoucherSummary[];
}

/**
 * Convert a paid checkout into voucher ROWS. Idempotent on the proof's opaque
 * payment id, so it is safe to call again after a lost response — a replay
 * lands on the same rows rather than minting new ones.
 *
 * Returns no codes (registration-wallets §4.3): the buyer's client minted them
 * before the checkout opened and committed to their hashes inside the proof, so
 * the codes to display are the ones in the caller's own stash. A replay cannot
 * hand them back either — which is exactly why the storefront keeps them.
 */
export async function purchaseVouchers(req: VoucherPurchaseRequest): Promise<VoucherBatchResponse> {
    return api.post<VoucherBatchResponse>('/api/voucher/purchase', req);
}

/**
 * What an `RGV-` code turns out to be, addressed by the lookup id derived from
 * it on this device. Anonymous and weaker than `register/begin`: it draws no
 * CID and changes nothing.
 *
 * Every non-hit — malformed id, unknown id — is the SAME 401, so this is not an
 * oracle for which vouchers exist. Callers surface that as "we do not recognise
 * this code" and never as "that voucher is gone".
 */
export async function inspectVoucher(voucherLookupId: string): Promise<VoucherInspectResponse> {
    return api.post<VoucherInspectResponse>('/api/voucher/inspect', { voucherLookupId });
}

/** Operator mint: the offline-sales, comp and support-replacement path. */
export async function mintVouchers(req: {
    quantity: number;
    registrationType: RegistrationType;
    note?: string;
    validityDays?: number;
    /** Fund each code's lock with an Administrator gift through the node's grant key (M12). */
    gift?: boolean;
}): Promise<VoucherMintResponse> {
    return api.post<VoucherMintResponse>('/api/voucher/mint', req);
}

/** Operator listing. Never returns codes — only hashes exist server-side. */
export async function listVouchers(params: {
    status?: VoucherStatus | '';
    registrationType?: RegistrationType | '';
    limit?: number;
    offset?: number;
} = {}): Promise<VoucherListResponse> {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.registrationType) q.set('registrationType', params.registrationType);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    const query = q.toString();
    return api.get<VoucherListResponse>(`/api/voucher${query ? `?${query}` : ''}`);
}

/** Revoke an UNREDEEMED voucher. Reserved and consumed ones are refused by the
 *  server — a registration is already riding on them. */
export async function revokeVoucher(
    voucherId: number, note?: string,
): Promise<{ voucherId: number; status: 'revoked' }> {
    return api.post(`/api/voucher/${voucherId}/revoke`, { note });
}

// ── Organization voucher ledger (registration-restructure §13.4) ─────────────

/** One row of an organization's own voucher ledger. Deliberately narrower than
 *  the operator's {@link VoucherSummary}: an organization sees what it bought and
 *  what became of it, not the node's Stripe bookkeeping. */
export interface OrgVoucherSummary {
    voucherId: number;
    registrationType: RegistrationType;
    status: VoucherStatus;
    provenance: 'payment' | 'operator';
    amount: number;
    currency: string;
    createdAt: string;
    expiresAt: string;
    reservedAt: string | null;
    consumedAt: string | null;
    revokedAt: string | null;
    redeemedByCid: string | null;
    /** The invitation this voucher backs. Always null until M15 lands — the field
     *  exists now so the ledger renders one row shape either way. */
    invitationId: string | null;
}

export interface OrgVoucherListResponse {
    total: number;
    limit: number;
    offset: number;
    vouchers: OrgVoucherSummary[];
}

/**
 * The organization's own voucher ledger — owner/admin only, delegate session.
 * Lists rows BOUND to this organization; unbound stock (an operator comp, an
 * anonymously bought delegate code) belongs to whoever holds the code and never
 * appears here. Never returns codes: only hashes exist server-side.
 */
export async function listOrganizationVouchers(
    orgCid: string,
    params: { limit?: number; offset?: number } = {},
): Promise<OrgVoucherListResponse> {
    const q = new URLSearchParams();
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    const query = q.toString();
    return api.get<OrgVoucherListResponse>(
        `/api/organization/${encodeURIComponent(orgCid)}/vouchers${query ? `?${query}` : ''}`);
}
